/**
 * Path Hint Row — location + goal + item drop slots.
 * Replaces the old PathHint.tsx with Tailwind + context.
 */
import { useTracker, type OotItemDto, type GoalDto } from '@/contexts/tracker-context';
import { LocationAutocomplete } from './location-autocomplete';
import { GoalAutocomplete } from './goal-autocomplete';
import { ItemDropSlot } from './item-drop-slot';
import type { OotItemData } from './data/oot-items-data';
import type { LocationDto } from '@/queries/types';

interface PathHintRowProps {
  id: number;
}

export function PathHintRow({ id }: PathHintRowProps) {
  const { state, dispatch } = useTracker();
  const goal = state.goals[id] ?? {
    location: null,
    goal: null,
    items: [],
    count: 0,
    player: null,
  };

  const setGoal = (updates: Partial<typeof goal>) => {
    dispatch({
      type: 'SET_GOAL',
      id,
      goal: { ...goal, ...updates },
    });
  };

  const handleLocationChange = (loc: LocationDto | null) => {
    setGoal({
      location: loc ? { id: loc.id, name: loc.name, areaName: loc.areaName } : null,
    });
  };

  const handleGoalChange = (g: { name: string; tags: string[]; img: string } | null) => {
    setGoal({
      goal: g ? { name: g.name, tags: g.tags, img: g.img } : null,
    });
  };

  const handleItemChange = (index: number, item: OotItemData | null) => {
    const items = [...goal.items];
    while (items.length <= index) items.push(null as unknown as OotItemDto);
    items[index] = item as unknown as OotItemDto;
    setGoal({ items });
  };

  const handleCountClick = () => {
    setGoal({ count: (goal.count + 1) % 3 });
  };

  const handleCountRightClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setGoal({ count: goal.count <= 0 ? 2 : goal.count - 1 });
  };

  // Build a LocationDto-compatible object for the autocomplete value
  const locationValue = goal.location
    ? ({
        id: goal.location.id,
        name: goal.location.name,
        areaName: goal.location.areaName,
      } as LocationDto)
    : null;

  const nbSlots = state.isMultiworld ? 7 : 5;

  return (
    <div className="flex items-center gap-1 py-0.5 px-2 bg-gray-800/50 rounded-md">
      {/* Counter */}
      <button
        className="w-5 text-center text-sm font-bold text-zelda-gold cursor-pointer select-none hover:text-white transition-colors"
        onClick={handleCountClick}
        onContextMenu={handleCountRightClick}
      >
        {goal.count}
      </button>

      {/* Location */}
      <LocationAutocomplete
        label="Location"
        value={locationValue}
        onChange={handleLocationChange}
        width="w-[150px]"
      />

      {/* Goal (hidden in WotH mode) */}
      {!state.isWoth && (
        <GoalAutocomplete
          value={goal.goal as unknown as { name: string; tags: string[]; img: string } | null}
          onChange={handleGoalChange}
        />
      )}

      {/* Player selector (multiworld) */}
      {state.isMultiworld && (
        <div className="text-xs text-gray-400 w-[60px]">
          {goal.player ?? 'P?'}
        </div>
      )}

      {/* Item drop slots */}
      <div className="flex gap-1 ml-auto">
        {Array.from({ length: nbSlots }, (_, i) => (
          <ItemDropSlot
            key={i}
            item={goal.items[i] as unknown as OotItemData | null}
            onItemChange={(item) => handleItemChange(i, item)}
            woth={state.isWoth}
          />
        ))}
      </div>
    </div>
  );
}
